import React, { useRef, useState, useEffect, useCallback } from 'react';

import { observer } from 'mobx-react';
import classnames from 'classnames';
import { Spinner } from '@bringg/react-components';
import {
	PanelContent,
	ResizableHandle,
	useMinSize,
	ImperativePanelGroupHandle,
	ImperativePanelHandle,
	PanelGroup
} from '@bringg-frontend/resizable-panel';
import { useRoutesMonitoringTranslations } from '@bringg-frontend/monitoring';
import { usePrevious } from '@bringg-frontend/hooks';

import MonitoringTable from '../monitoring-table/monitoring-table';
import DetailsTable from '../details-table/details-table';
import TasksTimeline from '../tasks-timeline/tasks-timeline';
import RoutesMap from '../map/routes-map';
import { calcSectionsSizes, defaultSizes, FULL_SECTION_WIDTH, MonitoringPanels } from '../utils/resizable-panels';
import { routesMonitoringRootStore, withRoutesMonitoringRootStore } from '../stores/routes-monitoring-root-store';
import useRouteMonitoringStore from '../stores/helpers/use-route-monitoring-store';
import { routesMonitoringPolling } from '../utils/routes-monitoring-polling';
import TopSection from '../top-section/top-section';

import styles from './routes-monitoring.module.scss';

export const RUNS_MONITORING_CLASS_NAME = 'runs-monitoring';

const RoutesMonitoring = () => {
	const groupPanelRef = useRef<ImperativePanelGroupHandle>(null);
	const detailsPanelRef = useRef<ImperativePanelHandle>(null);
	const [sizes, setSizes] = useState([]);
	const [showFilters, setShowFilters] = useState<boolean>(false);
	const { routesMonitoringStore, routesMonitoringTableViewStore, routesMonitoringViewStore } =
		useRouteMonitoringStore();
	const { loading, details, timeline, map } = useRoutesMonitoringTranslations();

	useEffect(() => {
		routesMonitoringPolling.start(routesMonitoringStore.refreshData);

		return () => {
			routesMonitoringViewStore.resetScroll();
			routesMonitoringRootStore.clearStore();
		};
	}, []);

	const prevViewedRoutes = usePrevious(routesMonitoringTableViewStore.viewedRoutes);

	useEffect(() => {
		if (
			!(prevViewedRoutes || []).length &&
			routesMonitoringTableViewStore?.viewedRoutes.length &&
			sizes[MonitoringPanels.MAP] === 0
		) {
			const sizesToSet = calcSectionsSizes(sizes, MonitoringPanels.MAP, false);
			groupPanelRef.current.setLayout(sizesToSet);
		}
	}, [prevViewedRoutes, routesMonitoringTableViewStore.viewedRoutes, sizes]);

	const minSize = useMinSize(24, '[data-panel-group-id="routes-monitoring"]');

	const layoutChange = useCallback(
		(panel: MonitoringPanels, collapse: boolean) => {
			const sizesToSet = calcSectionsSizes(sizes, panel, collapse);

			groupPanelRef.current.setLayout(sizesToSet);
		},
		[sizes]
	);

	const onDetailsColumnsChange = (sizeRatioChange: number) => {
		if (sizes[MonitoringPanels.ROUTES_DETAILS] !== FULL_SECTION_WIDTH) {
			detailsPanelRef.current.resize(detailsPanelRef.current.getSize() * sizeRatioChange);
		}
	};

	return (
		<div data-test-id="routes-monitoring" className={classnames(RUNS_MONITORING_CLASS_NAME, styles.pageContainer)}>
			<TopSection showFilters={showFilters} onToggleFilterDisplay={setShowFilters} />
			<div
				className={classnames({
					[styles.panelGroupContainer]: true,
					[styles.footerOpen]: routesMonitoringStore.hasPagination
				})}
			>
				<Spinner tip={loading} spinning={routesMonitoringStore.loading} className={styles.spinner}>
					<PanelGroup
						direction="horizontal"
						className={styles.PanelGroup}
						autoSaveId="routes-monitoring"
						id="routes-monitoring"
						disablePointerEventsDuringResize={true}
						ref={groupPanelRef}
						onLayout={setSizes}
					>
						<PanelContent
							collapsible={false}
							minSize={10}
							defaultSize={defaultSizes[MonitoringPanels.ROUTES]}
						>
							<MonitoringTable />
						</PanelContent>
						<ResizableHandle />
						<PanelContent
							sectionName={details}
							defaultSize={defaultSizes[MonitoringPanels.ROUTES_DETAILS]}
							minSize={minSize}
							onPanelCollapse={(collapse: boolean) =>
								layoutChange(MonitoringPanels.ROUTES_DETAILS, collapse)
							}
							ref={detailsPanelRef}
						>
							<DetailsTable
								onColumnsChange={onDetailsColumnsChange}
								isScrolled={routesMonitoringViewStore.isScrolled}
							/>
						</PanelContent>
						<ResizableHandle />
						<PanelContent
							sectionName={timeline}
							minSize={minSize}
							onPanelCollapse={(collapse: boolean) => layoutChange(MonitoringPanels.TIME_LINE, collapse)}
							defaultSize={defaultSizes[MonitoringPanels.TIME_LINE]}
						>
							<TasksTimeline />
						</PanelContent>
						<ResizableHandle />
						<PanelContent
							sectionName={map}
							defaultSize={defaultSizes[MonitoringPanels.MAP]}
							minSize={minSize}
							onPanelCollapse={(collapse: boolean) => layoutChange(MonitoringPanels.MAP, collapse)}
							unmountOnCollapse
						>
							<RoutesMap />
						</PanelContent>
					</PanelGroup>
				</Spinner>
			</div>
		</div>
	);
};

export default withRoutesMonitoringRootStore(observer(RoutesMonitoring));
